import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import beds24Logo from '../../assets/images/partners/beds24.jpeg';
import staymiiLogo from '../../assets/images/staymii_logo.png';
import {
  ArrowRight, Check, ChevronDown, Shield, Zap, Receipt,
  Plug, Clock, FileText, BarChart3, Lock, RefreshCw,
  Building2, Users, Globe, AlertTriangle, CheckCircle2
} from 'lucide-react';

const h = { fontFamily: "'Outfit', 'Plus Jakarta Sans', sans-serif" };
const b = { fontFamily: "'IBM Plex Sans', 'Manrope', sans-serif" };

/* ─── Reveal on scroll ─── */
function Reveal({ children, delay = 0 }) {
  const ref = React.useRef(null);
  const [vis, setVis] = React.useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? 'translateY(0)' : 'translateY(24px)', transition: `all 0.6s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

const Beds24Integration = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [openFaq, setOpenFaq] = useState([1]);
  const toggleFaq = (id) => setOpenFaq(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const faqs = [
    { id: 1, q: 'How does StayMii connect to my Beds24 account?', a: 'StayMii connects via the official Beds24 API. You simply authorize StayMii from your Beds24 dashboard — no plugins, no downloads. The connection is established in under 2 minutes.' },
    { id: 2, q: 'Will StayMii change anything in my Beds24 setup?', a: 'No. StayMii is read-only by default. It pulls booking and payment data to generate e-invoices but never modifies your Beds24 bookings, rates, or availability. Your operations stay untouched.' },
    { id: 3, q: 'What data does StayMii pull from Beds24?', a: 'StayMii syncs booking details (guest name, dates, room type, payment amounts) to auto-generate LHDN-compliant e-invoices. No sensitive payment card data is accessed.' },
    { id: 4, q: 'Is the integration available now?', a: 'Yes. The Beds24 integration is officially live from 23 October 2026, following a soft launch on 16 July 2026. You can sign up and connect your Beds24 account today.' },
    { id: 5, q: 'Do I need to change my Beds24 plan?', a: 'No. StayMii works with all Beds24 plans that include API access. There are no changes required on the Beds24 side.' },
    { id: 6, q: 'What if I have multiple properties on Beds24?', a: 'StayMii supports multi-property setups. Each property syncs independently, and you can manage all your e-invoices from a single StayMii dashboard.' },
  ];

  return (
    <div className="min-h-screen bg-white" style={b} data-testid="beds24-integration-page">
      <SEO
        title="Beds24 + StayMii — Automated LHDN E-Invoicing for Your Hotel"
        description="Connect Beds24 to StayMii and automate Malaysia LHDN e-invoicing compliance. No system change. No manual work. Go live in minutes."
        keywords="beds24 e-invoice malaysia, beds24 lhdn integration, hotel e-invoicing malaysia, beds24 staymii"
        canonicalUrl="/integrations/beds24"
      />
      <LandingNavBar />

      {/* ═══ HERO ═══ */}
      <section className="relative pt-32 pb-24 md:pb-32 overflow-hidden" data-testid="beds24-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-blue-100/20 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Co-branding */}
              <Reveal>
                <div className="flex items-center gap-4 mb-8" data-testid="co-branding">
                  <img src={beds24Logo} alt="Beds24" className="h-10 w-auto rounded-lg" />
                  <span className="text-2xl text-slate-300 font-light">+</span>
                  <img src={staymiiLogo} alt="StayMii" className="h-10 w-auto rounded-lg" />
                </div>
              </Reveal>

              <Reveal delay={80}>
                <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-bold text-emerald-700 uppercase tracking-[0.15em] mb-6">
                  <Shield className="w-3.5 h-3.5" /> Official Integration — Coming June 2026
                </span>
              </Reveal>

              <Reveal delay={120}>
                <h1 className="text-5xl sm:text-6xl font-semibold text-slate-950 tracking-tighter leading-tight mb-6" style={h}>
                  Automate LHDN<br />e-Invoicing for Your<br />Beds24 Properties
                </h1>
              </Reveal>

              <Reveal delay={180}>
                <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mb-10">
                  Connect your Beds24 account and let StayMii handle Malaysia's e-invoicing requirements automatically. No migration. No manual work. Fully LHDN-compliant from day one.
                </p>
              </Reveal>

              <Reveal delay={240}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/einvoice/portal/auth"
                    className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-slate-950 hover:bg-slate-800 text-white rounded-2xl font-semibold text-base transition-all"
                    data-testid="beds24-hero-cta"
                  >
                    Get Started <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-2xl font-semibold text-base transition-all"
                    data-testid="beds24-hero-demo"
                  >
                    Book a Demo
                  </Link>
                </div>
              </Reveal>
            </div>

            {/* Right side — Logos */}
            <Reveal delay={200}>
              <div className="hidden lg:flex items-center justify-center">
                <div className="flex items-center gap-8 p-10 bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/50">
                  <img src={beds24Logo} alt="Beds24" className="h-20 w-auto rounded-xl" />
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-12 h-[2px] bg-slate-300 rounded" />
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">integrated</span>
                    <div className="w-12 h-[2px] bg-slate-300 rounded" />
                  </div>
                  <img src={staymiiLogo} alt="StayMii" className="h-20 w-auto rounded-xl" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ WHY THIS MATTERS ═══ */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="beds24-why">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="max-w-3xl mb-16">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-4 block">The Problem</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight leading-snug mb-4" style={h}>
                Beds24 doesn't handle Malaysia's<br />e-invoicing requirements. StayMii does.
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                LHDN now requires all hospitality businesses to generate and submit e-invoices digitally. Beds24 is a world-class PMS — but it wasn't built for Malaysian tax compliance. StayMii bridges that gap.
              </p>
            </div>
          </Reveal>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal delay={60}>
              <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-all h-full" data-testid="bento-compliance">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-6">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-950 mb-3 tracking-tight" style={h}>100% LHDN Compliant</h3>
                <p className="text-slate-600 leading-relaxed">
                  Every e-invoice is validated against LHDN MyInvois standards before submission. Formatting, tax codes, TIN validation — all handled automatically. Zero risk of rejection.
                </p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-all h-full" data-testid="bento-sync">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                  <RefreshCw className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-950 mb-3 tracking-tight" style={h}>Real-Time Sync</h3>
                <p className="text-slate-600 leading-relaxed">
                  Bookings flow from Beds24 to StayMii automatically. No CSV exports, no copy-pasting, no delays.
                </p>
              </div>
            </Reveal>
            <Reveal delay={180}>
              <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-all h-full" data-testid="bento-zero">
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-950 mb-3 tracking-tight" style={h}>Zero Disruption</h3>
                <p className="text-slate-600 leading-relaxed">
                  StayMii is read-only. It never modifies your Beds24 setup. Your rates, availability, and bookings stay exactly as they are.
                </p>
              </div>
            </Reveal>
            <Reveal delay={240}>
              <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-all h-full" data-testid="bento-multi">
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-6">
                  <Building2 className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-950 mb-3 tracking-tight" style={h}>Multi-Property Ready</h3>
                <p className="text-slate-600 leading-relaxed">
                  Manage e-invoices for all your Beds24 properties from one StayMii dashboard. Perfect for chains, management companies, and operators running multiple sites.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="py-24 md:py-32 bg-white" data-testid="beds24-how">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-20">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-4 block">How It Works</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight leading-snug mb-4" style={h}>
                Three steps to full compliance
              </h2>
              <p className="text-lg text-slate-600">Connect, automate, and submit — all in under 5 minutes.</p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-[56px] left-[16.7%] w-[66.6%] h-px bg-slate-200" />

            {[
              { num: '01', title: 'Connect Beds24', desc: 'Authorize StayMii from your Beds24 account. One click, two minutes, no technical setup.', icon: Plug, color: 'bg-blue-600' },
              { num: '02', title: 'Auto-Generate Invoices', desc: 'Every booking syncs automatically. StayMii generates LHDN-compliant e-invoices in real time.', icon: Receipt, color: 'bg-slate-950' },
              { num: '03', title: 'Submit to LHDN', desc: 'StayMii submits directly to MyInvois, tracks status, and stores audit-ready records.', icon: Shield, color: 'bg-emerald-600' },
            ].map((step, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="relative text-center" data-testid={`beds24-step-${step.num}`}>
                  <div className={`w-14 h-14 ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg relative z-10`}>
                    <step.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-6xl font-bold text-slate-100 absolute top-[-12px] left-1/2 -translate-x-1/2 z-0 select-none" style={h}>{step.num}</span>
                  <h3 className="text-lg font-semibold text-slate-950 mb-2 mt-2" style={h}>{step.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ LIVE DASHBOARD PREVIEW ═══ */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="beds24-dashboard">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-4 block">Live Preview</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight leading-snug mb-4" style={h}>
                See it in action
              </h2>
              <p className="text-lg text-slate-600">Your Beds24 bookings become compliant e-invoices automatically.</p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_5e74271a-8fbb-4f1f-8b01-d4ef0a762567/artifacts/3guzdbw8_staymii_demo_clean.gif"
                alt="StayMii dashboard — Beds24 bookings synced as e-invoices"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <p className="text-center text-xs text-slate-400 mt-4 font-medium">StayMii Dashboard — Beds24 bookings auto-converted to LHDN-compliant e-invoices</p>
          </Reveal>
        </div>
      </section>

      {/* ═══ COMPLIANCE STATS ═══ */}
      <section className="py-24 md:py-32 bg-white" data-testid="beds24-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-16">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-4 block">By The Numbers</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight mb-4" style={h}>
                Built for scale. Proven for compliance.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { num: '100%', label: 'LHDN Compliant', sub: 'Every invoice validated' },
              { num: '<2min', label: 'Setup Time', sub: 'Connect & go live' },
              { num: '0', label: 'Manual Entry', sub: 'Fully automated' },
              { num: '24/7', label: 'Sync Monitoring', sub: 'Always up to date' },
            ].map((stat, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-100" data-testid={`beds24-stat-${i}`}>
                  <p className="text-5xl md:text-6xl font-bold text-slate-950 mb-2 tracking-tighter" style={h}>{stat.num}</p>
                  <p className="text-sm font-semibold text-slate-700 mb-1">{stat.label}</p>
                  <p className="text-xs text-slate-400">{stat.sub}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ KEY BENEFITS FOR BEDS24 USERS ═══ */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="beds24-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div>
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-4 block">For Beds24 Users</span>
                <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight leading-snug mb-6" style={h}>
                  Everything you need.<br />Nothing you don't.
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-10">
                  StayMii adds the compliance layer your Beds24 setup is missing — without changing how you work.
                </p>

                <div className="space-y-4">
                  {[
                    'Auto-sync bookings from all Beds24 properties',
                    'Generate LHDN-compliant e-invoices instantly',
                    'Submit to MyInvois with one-click or auto-submit',
                    'Real-time status tracking and audit trail',
                    'PDF + QR code output for every invoice',
                    'Bulk invoice processing (50+ at once)',
                    'Multi-property dashboard',
                    'Customer TIN management',
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-emerald-50 rounded-full flex items-center justify-center shrink-0">
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <span className="text-slate-700 text-sm font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-10">
                <div className="flex items-center gap-3 mb-8">
                  <img src={beds24Logo} alt="Beds24" className="h-8 rounded" />
                  <ArrowRight className="w-4 h-4 text-slate-300" />
                  <img src={staymiiLogo} alt="StayMii" className="h-8 rounded" />
                  <ArrowRight className="w-4 h-4 text-slate-300" />
                  <span className="text-lg font-bold text-emerald-600" style={h}>LHDN</span>
                </div>

                {/* Mini flow demo */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                      <Globe className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">Booking from Booking.com</p>
                      <p className="text-xs text-slate-400">via Beds24 &middot; RM 480</p>
                    </div>
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">Synced</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">INV-2026-003291</p>
                      <p className="text-xs text-slate-400">e-Invoice generated &middot; LHDN format</p>
                    </div>
                    <span className="px-2.5 py-1 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-full">Valid</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">Submitted to LHDN MyInvois</p>
                      <p className="text-xs text-emerald-600 font-medium">Status: Accepted</p>
                    </div>
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="py-24 md:py-32 bg-white" data-testid="beds24-faq">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-4 block">FAQ</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-950 tracking-tight mb-4" style={h}>
                Common Questions
              </h2>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="space-y-3">
              {faqs.map((faq, idx) => {
                const isOpen = openFaq.includes(faq.id);
                return (
                  <div
                    key={faq.id}
                    className={`rounded-2xl overflow-hidden transition-all duration-300 ${
                      isOpen ? 'bg-white border border-slate-200 shadow-lg shadow-slate-100/50' : 'bg-white/60 border border-slate-100 hover:bg-white hover:border-slate-200'
                    }`}
                    data-testid={`beds24-faq-${faq.id}`}
                  >
                    <button onClick={() => toggleFaq(faq.id)} className="w-full flex items-center gap-4 p-6 text-left">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${isOpen ? 'bg-slate-950' : 'bg-slate-100'}`}>
                        <span className={`text-xs font-bold ${isOpen ? 'text-white' : 'text-slate-400'}`}>{String(idx + 1).padStart(2, '0')}</span>
                      </div>
                      <span className={`flex-1 font-semibold text-base ${isOpen ? 'text-slate-950' : 'text-slate-700'}`}>{faq.q}</span>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div style={{ maxHeight: isOpen ? '400px' : '0', opacity: isOpen ? 1 : 0, overflow: 'hidden', transition: 'all 0.4s ease' }}>
                      <div className="px-6 pb-6 pl-[72px]">
                        <p className="text-sm text-slate-500 leading-relaxed">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ BOTTOM CTA ═══ */}
      <section className="py-24 md:py-32 bg-slate-950" data-testid="beds24-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-4 mb-8">
              <img src={beds24Logo} alt="Beds24" className="h-10 rounded-lg" />
              <span className="text-2xl text-slate-600 font-light">+</span>
              <img src={staymiiLogo} alt="StayMii" className="h-10 rounded-lg" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-semibold text-white tracking-tighter leading-tight mb-6" style={h}>
              Ready to automate your<br />e-invoicing compliance?
            </h2>
            <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Join Beds24 operators across Malaysia who are preparing for seamless LHDN compliance with StayMii.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/einvoice/portal/auth"
                className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-white text-slate-950 hover:bg-slate-100 rounded-2xl font-semibold text-base transition-all"
                data-testid="beds24-bottom-cta"
              >
                Get Started <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-slate-600 text-slate-300 hover:bg-slate-800 rounded-2xl font-semibold text-base transition-all"
                data-testid="beds24-bottom-demo"
              >
                Contact Sales
              </Link>
            </div>
            <p className="text-slate-600 text-xs mt-8">Officially live since 23 October 2026 &middot; Available now</p>
          </Reveal>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default Beds24Integration;
